import React from "react";
import { Modal, Button } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiDeleteOutline,
  mdiOpenInNew,
  mdiPencilOutline,
  mdiPlus,
} from "@mdi/js";
import InfoTooltip from "app/components/common/InfoTooltip";
import moment from "moment-timezone";
import { TOOLTIP_DELAY } from "./InfoIconSlot";
import JobPopoverPeople from "./JobPopoverPeople";
import { JobResultsInfo, JobSetupInfo } from "./JobPopoverJobInfo";
import { hasJobSetup } from "app/containers/JobCalendar/utils/jobSetupMode";
import "./JobPopover.css";

const JobPopover = ({
  show,
  onHide,
  appointment,
  onDelete,
  onJobBooking,
  onJobSetup,
  loading = false,
  anomalies,
  onViewJobDetail,
}) => {
  // Early return if no appointment
  if (!appointment) return null;

  // Extract status from appointment
  const status = appointment?.tag || appointment?.status || "booked";

  // Status colors live in jobPopover.css as --<status> modifier classes.
  const getStatusInfo = (status) => {
    const normalizedStatus = status?.toLowerCase();
    switch (normalizedStatus) {
      case "booked":
        return { modifier: "booked", text: "Booked" };
      case "pre-inspection":
        return { modifier: "pre-inspection", text: "Pre-Inspection" };
      case "under-inspection":
        return { modifier: "under-inspection", text: "Under Inspection" };
      case "completed":
        return { modifier: "completed", text: "Completed" };
      default:
        return { modifier: "booked", text: "Booked" };
    }
  };

  const statusInfo = getStatusInfo(status);

  // Check if it's mobile and truncate status text if needed
  const isMobile = window.innerWidth <= 480;
  const displayStatusText =
    isMobile && statusInfo.text.length > 6
      ? statusInfo.text.substring(0, 3) + "..."
      : statusInfo.text;

  // Format time display
  const formatTimeDisplay = () => {
    try {
      const startTime = moment(
        (appointment.start_time || appointment.start).substring(0, 19)
      );
      const endTime = moment(
        (appointment.end_time || appointment.end).substring(0, 19)
      );
      return `${startTime.format("MMM D, h:mm A")} - ${endTime.format("h:mm A")}`;
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Time unavailable";
    }
  };

  // Compute display data
  const timeDisplay = formatTimeDisplay();
  const normalizedStatus = status.toLowerCase();
  const isSetUp = hasJobSetup(appointment);

  return (
    <Modal show={show} onHide={onHide} centered className="job-popover-modal">
      <Modal.Header closeButton={false} className="job-popover-header">
        <div className="job-popover-time">
          <span>{timeDisplay}</span>
          {appointment.note && (
            <InfoTooltip
              text={appointment.note}
              delay={TOOLTIP_DELAY}
              className="custom-tooltip"
              iconSize="20px"
              iconClassName="job-popover-time-info"
            />
          )}
        </div>
        <div
          className={`job-popover-status-badge job-popover-status-badge--${statusInfo.modifier}`}
        >
          {displayStatusText}
        </div>
      </Modal.Header>

      <Modal.Body className="job-popover-body">
        {loading ? (
          <div className="job-popover-loading">
            <div className="d-flex justify-content-center align-items-center job-popover-loading-body">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  Loading appointment details...
                </span>
              </div>
              <span className="ms-3">Loading appointment details...</span>
            </div>
          </div>
        ) : (
          <div className="job-popover-content">
            <JobPopoverPeople appointment={appointment} />

            {isSetUp && <JobSetupInfo appointment={appointment} />}

            {anomalies.length > 0 && (
              <div className="job-anomaly-banner">
                <Icon
                  path={mdiAlertCircleOutline}
                  size="16px"
                  className="job-anomaly-banner-icon"
                />
                <span className="job-anomaly-banner-text">
                  Anomaly: {anomalies.join(", ")}
                </span>
                <Button
                  variant="link"
                  className="job-anomaly-banner-link"
                  onClick={onViewJobDetail}
                >
                  <Icon path={mdiOpenInNew} size="16px" />
                  View Job Detail
                </Button>
              </div>
            )}

            <JobResultsInfo
              appointment={appointment}
              showReport={normalizedStatus === "completed"}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="job-popover-footer">
        <Button
          variant="outline-danger"
          size="sm"
          className="job-popover-delete-btn"
          onClick={() => onDelete && onDelete(appointment)}
          disabled={loading}
          aria-label="Delete job"
        >
          <Icon path={mdiDeleteOutline} size="20px" />
        </Button>

        <div className="job-popover-footer-actions">
          <Button
            variant="outline-secondary"
            size="sm"
            className="job-popover-booking-btn"
            onClick={() => onJobBooking && onJobBooking(appointment)}
            disabled={loading}
          >
            <Icon path={mdiPencilOutline} size="16px" />
            Job Booking
          </Button>

          <Button
            variant={isSetUp ? "outline-secondary" : "primary"}
            size="sm"
            className="job-popover-setup-btn"
            onClick={() => onJobSetup && onJobSetup(appointment)}
            disabled={loading}
          >
            <Icon path={isSetUp ? mdiPencilOutline : mdiPlus} size="16px" />
            Job Setup
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default JobPopover;
